import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    RefreshCw,
    Layout,
    ChevronDown,
    ChevronUp,
    Mountain,
    MapPin,
    Image as ImageIcon,
    Target,
    Plane,
    Truck,
    Info,
    Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminHeader: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({});
    const [expandedSections, setExpandedSections] = useState<string[]>(['Core Navigation']);
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const toggleSection = (name: string) => {
        setExpandedSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
        );
    };

    const saveSectionText = async (sectionName: string) => {
        setSaving(sectionName);
        const sectionData = content[sectionName] || {};

        try {
            const mainKeys = Object.keys(sectionData).filter(key => !key.endsWith('_ar'));

            for (const key of mainKeys) {
                const value = sectionData[key];
                const valueAr = sectionData[`${key}_ar`] || null;
                if (typeof value !== 'string') continue;

                const res = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({ 
                        section: sectionName, 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: valueAr
                    })
                });
                
                if (res.status === 401 || res.status === 403) {
                    alert('Your session has expired. Please login again to save your changes.');
                    handleLogout();
                    return;
                }
            }
        } catch (err) {
            console.error(`Save failed for ${sectionName}`);
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const handleImageSelect = (section: string, key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [`${section}-${key}`]: { file, preview }
        }));
    };

    const saveAllPendingImages = async () => {
        const totalPending = Object.keys(pendingImages).length;
        if (totalPending === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const parts = key.split('-');
                const section = parts[0];
                const keyName = parts.slice(1).join('-');

                const formData = new FormData();
                formData.append('section', section);
                formData.append('content_key', keyName);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    return { section, keyName, imageUrl: data.imageUrl };
                } else {
                    const errorData = await response.json();
                    console.error(`Upload failed for ${section}-${keyName}:`, errorData);
                    return null;
                }
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => {
                const next = { ...prev };
                results.forEach(res => {
                    if (res) {
                        next[res.section] = {
                            ...(next[res.section] || {}),
                            [res.keyName]: res.imageUrl
                        };
                    }
                });
                return next;
            });

            setPendingImages({});
        } catch (err) {
            console.error('Batch upload failed');
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const UploadBox = ({ section, keyName, label, currentImage, aspect = "aspect-video" }: any) => {
        const isIndividualUploading = saving === `${section}-${keyName}`;
        const isBatchUploading = saving === 'all-images';
        const pending = pendingImages[`${section}-${keyName}`];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[8px] font-black uppercase">Live</span>
                        </div>
                    )}
                    {pending && (
                        <div className="flex items-center gap-1.5 text-amber-500 bg-amber-50 px-2 py-1 rounded-lg">
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                            <span className="text-[8px] font-black uppercase">Pending</span>
                        </div>
                    )}
                </div>

                <div className={`relative ${aspect} bg-slate-50 rounded-2xl overflow-hidden mb-6 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all shadow-inner`}>
                    {displayImage ? (
                        <img src={displayImage} className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105 p-4" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-10 h-10 mb-2 opacity-20" />
                            <span className="text-[10px] font-black uppercase tracking-widest">No Asset Loaded</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold hover:scale-110 transition-transform shadow-2xl flex items-center space-x-3">
                            <RefreshCw className="w-5 h-5" />
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageSelect(section, keyName, file);
                                }}
                            />
                        </label>
                    </div>
                    {(isIndividualUploading || isBatchUploading) && (
                        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3" />
                            <span className="text-[10px] font-black text-blue-600 uppercase">Syncing...</span>
                        </div>
                    )}
                </div>
                {(currentImage || pending) && (
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                        <span className="text-[8px] text-slate-400 font-bold uppercase tracking-tighter truncate max-w-[140px]">
                            {pending ? pending.file.name : (currentImage ? currentImage.split('/').pop() : '')}
                        </span>
                        <span className="text-[8px] text-blue-400 font-black">
                            {pending ? `${(pending.file.size / 1024).toFixed(0)} KB` : "Web Optimized"}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const SectionHeader = ({ title, icon: Icon, sectionKey }: any) => (
        <div className="flex items-center justify-between p-6 bg-white rounded-3xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => toggleSection(title)}>
                <div className={`p-3 rounded-2xl transition-colors ${expandedSections.includes(title) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Configuration Panel</p>
                </div>
                {expandedSections.includes(title) ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
            {sectionKey && (
                <button
                    onClick={() => saveSectionText(sectionKey)}
                    disabled={saving === sectionKey}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-emerald-100 active:scale-95"
                >
                    {saving === sectionKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Sync Labels</span>
                </button>
            )}
        </div>
    );

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Fixed Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Layout className="w-6 h-6 text-blue-600" /> Header Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-40">
                            <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                            <p className="text-slate-400 font-black uppercase tracking-widest text-[10px]">Syncing Configuration...</p>
                        </div>
                    ) : (
                        <div className="max-w-6xl mx-auto">
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div
                                        key="text-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-6"
                                    >
                                        <SectionHeader title="Core Navigation" icon={Layout} sectionKey="Header" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Core Navigation') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-10 rounded-[32px] border border-slate-100 shadow-sm space-y-10 ml-4 mb-4">
                                                        <div>
                                                            <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs mb-6 border-b border-slate-50 pb-4">Main Menu Labels</h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                {['nav_home', 'country_oman', 'country_qatar', 'country_uae', 'nav_group', 'nav_about', 'nav_careers', 'nav_contact'].map(key => (
                                                                    <BilingualInput 
                                                                        key={key}
                                                                        label={key.replace('nav_', '').replace('country_', '').toUpperCase()}
                                                                        englishValue={content.Header?.[key] || ''}
                                                                        arabicValue={content.Header?.[`${key}_ar`]}
                                                                        onChangeEnglish={val => setContent({ ...content, Header: { ...(content.Header || {}), [key]: val } })}
                                                                        onChangeArabic={val => setContent({ ...content, Header: { ...(content.Header || {}), [`${key}_ar`]: val } })}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs mb-6 border-b border-slate-50 pb-4">Our Group Submenu</h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                                                                    <BilingualInput 
                                                                        key={`group_${i}`}
                                                                        label={`Division ${i}${i === 6 ? ' (Parent: Investments & Farm)' : ''}`}
                                                                        englishValue={content.Header?.[`group_${i}`] || ''}
                                                                        arabicValue={content.Header?.[`group_${i}_ar`]}
                                                                        onChangeEnglish={val => setContent({ ...content, Header: { ...(content.Header || {}), [`group_${i}`]: val } })}
                                                                        onChangeArabic={val => setContent({ ...content, Header: { ...(content.Header || {}), [`group_${i}_ar`]: val } })}
                                                                    />
                                                                ))}
                                                            </div>
                                                            {/* Sub-items (nested flyouts) */}
                                                            <div className="mt-6 pt-8 border-t border-slate-100">
                                                                <h5 className="font-black text-slate-500 uppercase tracking-widest text-[10px] mb-6">Sub-items (show on hover)</h5>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                    <BilingualInput 
                                                                        label="Division 3 Sub → Gabbro (under Mining)"
                                                                        englishValue={content.Header?.['group_3'] || ''}
                                                                        arabicValue={content.Header?.['group_3_ar']}
                                                                        onChangeEnglish={val => setContent({ ...content, Header: { ...(content.Header || {}), group_3: val } })}
                                                                        onChangeArabic={val => setContent({ ...content, Header: { ...(content.Header || {}), group_3_ar: val } })}
                                                                        placeholder="Hamdan International for Gabbro"
                                                                    />
                                                                    <BilingualInput 
                                                                        label="Division 6 Sub → Al Yasminah (under Investments)"
                                                                        englishValue={content.Header?.['group_6_sub_1'] || ''}
                                                                        arabicValue={content.Header?.['group_6_sub_1_ar']}
                                                                        onChangeEnglish={val => setContent({ ...content, Header: { ...(content.Header || {}), group_6_sub_1: val } })}
                                                                        onChangeArabic={val => setContent({ ...content, Header: { ...(content.Header || {}), group_6_sub_1_ar: val } })}
                                                                        placeholder="Al Yasminah Real Estate"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs mb-6 border-b border-slate-50 pb-4">About Us Submenu</h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                {[1, 2, 3, 4].map(i => (
                                                                    <BilingualInput 
                                                                        key={`about_${i}`}
                                                                        label={`Link ${i}`}
                                                                        englishValue={content.Header?.[`about_${i}`] || ''}
                                                                        arabicValue={content.Header?.[`about_${i}_ar`]}
                                                                        onChangeEnglish={val => setContent({ ...content, Header: { ...(content.Header || {}), [`about_${i}`]: val } })}
                                                                        onChangeArabic={val => setContent({ ...content, Header: { ...(content.Header || {}), [`about_${i}_ar`]: val } })}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="media-tab"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="space-y-12"
                                    >
                                        <div className="flex items-center justify-between border-b border-slate-100 pb-8">
                                            <div>
                                                <h3 className="text-3xl font-black text-slate-800 tracking-tight">Brand Assets</h3>
                                                <p className="text-slate-500 text-sm font-medium">Manage global header logos and branding</p>
                                            </div>
                                            <button
                                                onClick={saveAllPendingImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 active:scale-95 disabled:bg-slate-200"
                                            >
                                                {saving === 'all-images' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 inline mr-2" />}
                                                Push Brand Updates
                                            </button>
                                        </div>

                                        <div className="max-w-xl">
                                            <UploadBox
                                                section="Header"
                                                keyName="logo_image"
                                                label="Primary Corporate Logo"
                                                currentImage={content.Header?.logo_image}
                                                aspect="aspect-[3/1]"
                                            />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </main>
            </div>


        </div>
    );
};

export default AdminHeader;
